import { CheckCircle2, Cog, Lock, Loader2, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";

export type OpStepState = "waiting" | "active" | "done" | "error" | "blocked";

export type OpStepItem = {
  id: string;
  label: string;
  detail?: string;
  percent?: number;
  state: OpStepState;
};

export function OpStepRail({
  title = "Proces",
  now,
  steps,
  className,
  onStepClick,
}: {
  title?: string;
  now?: string;
  steps: OpStepItem[];
  className?: string;
  onStepClick?: (id: string) => void;
}) {
  return (
    <div className={cn("rounded-xl border-2 border-slate-300 bg-white p-4 shadow-md", className)}>
      <div className="mb-3">
        <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-cyan-800">
          <Cog className="h-3.5 w-3.5 text-cyan-700" aria-hidden />
          {title}
        </p>
        {now ? <p className="mt-1 text-sm font-semibold text-slate-900">{now}</p> : null}
      </div>

      <div className="mb-4 flex gap-1.5 xl:hidden">
        {steps.map((step) => (
          <button
            key={step.id}
            type="button"
            title={step.label}
            onClick={() => onStepClick?.(step.id)}
            className={cn(
              "h-2 flex-1 rounded-full",
              step.state === "done" && "bg-success",
              step.state === "active" && "animate-pulse bg-brand",
              step.state === "blocked" && "bg-muted",
              step.state === "error" && "bg-destructive",
              step.state === "waiting" && "bg-border",
            )}
          >
            <span className="sr-only">
              {step.label} {step.state}
            </span>
          </button>
        ))}
      </div>
      {steps.some((s) => s.state === "active") ? (
        <p className="mb-3 text-xs font-medium text-brand xl:hidden">
          {steps.find((s) => s.state === "active")?.label}
        </p>
      ) : null}

      <ol className="relative hidden space-y-0 xl:block">
        {steps.map((step, idx) => (
          <li key={step.id} className="relative flex gap-3 pb-5 last:pb-0">
            {idx < steps.length - 1 ? (
              <span className="absolute left-[19px] top-10 h-[calc(100%-16px)] w-0.5 bg-slate-200" />
            ) : null}
            <button
              type="button"
              onClick={() => onStepClick?.(step.id)}
              className={cn(
                "relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2",
                step.state === "done" && "border-success bg-success text-success-foreground",
                step.state === "active" && "border-brand bg-brand/15 text-brand",
                step.state === "blocked" && "border-slate-300 bg-muted text-muted-foreground",
                step.state === "error" && "border-destructive bg-destructive/10 text-destructive",
                step.state === "waiting" && "border-slate-300 bg-white text-muted-foreground",
              )}
              aria-label={step.label}
            >
              {step.state === "done" ? (
                <CheckCircle2 className="h-5 w-5" />
              ) : step.state === "blocked" ? (
                <Lock className="h-4 w-4" />
              ) : step.state === "error" ? (
                <XCircle className="h-5 w-5" />
              ) : step.state === "active" ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <span className="text-sm font-bold">{idx + 1}</span>
              )}
            </button>
            <button type="button" className="min-w-0 flex-1 text-left" onClick={() => onStepClick?.(step.id)}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-slate-900">{step.label}</span>
                {typeof step.percent === "number" ? (
                  <span className="text-xs text-muted-foreground">{step.percent}%</span>
                ) : null}
              </div>
              {step.detail ? <p className="mt-0.5 text-xs text-muted-foreground">{step.detail}</p> : null}
              {step.state === "active" ? (
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full bg-brand transition-all"
                    style={{ width: `${step.percent ?? 35}%` }}
                  />
                </div>
              ) : null}
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
}
